import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Package, Award, Zap } from 'lucide-react';
import { heroData, features, stats, testimonials } from '../mock';
import TestimonialSection from '../components/TestimonialSection';
import ProductBanner from '../components/ProductBanner';
import SEO from '../components/SEO';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Swedish Candy Wholesale Worldwide | Global Bulk Candy Supplier"
        description="Premium Swedish candy wholesale supplier with partners worldwide. Specializing in bulk wholesale Swedish candy, foamy candy, and skull candy. International B2B candy distributor serving retailers globally across North America, Europe, Asia, Australia, Middle East, South America & Africa. Making happy memories worldwide."
        keywords="Swedish candy, Swedish wholesale candy, bulk wholesale, foamy candy, skull candy, international candy supplier, global candy wholesaler, worldwide candy distributor, Swedish candy Europe, Swedish candy Asia"
        canonical={process.env.REACT_APP_BASE_URL || "https://katomito.com"}
      />
      {/* Product Banner - Rotating Showcase */}
      <ProductBanner />

      {/* Candy Features Highlights - Protein Candy Style */}
      <section className="py-12 bg-white border-b-2 border-kato-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-4">
            {/* TikTok Famous */}
            <div className="flex items-center px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform duration-300" style={{ background: 'linear-gradient(to right, #F5ED3A, #FFF8B8)' }}>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#221D58' }}>
                <path d="M19.321 5.562a5.122 5.122 0 01-.443-.258 6.228 6.228 0 01-1.137-.966c-.849-.963-1.248-2.049-1.248-3.388h-3.089v11.127c0 .984-.4 1.902-1.134 2.559a3.685 3.685 0 01-2.559 1.025c-2.034 0-3.693-1.659-3.693-3.693 0-2.034 1.659-3.693 3.693-3.693.373 0 .734.056 1.077.162V5.562a6.692 6.692 0 00-1.077-.087c-3.702 0-6.693 3.007-6.693 6.693 0 1.879.776 3.578 2.027 4.794A6.656 6.656 0 006.396 18c3.686 0 6.693-3.007 6.693-6.693V5.562h3.232z"/>
              </svg>
              <span className="font-black text-xs" style={{ color: '#221D58' }}>TIKTOK FAMOUS</span>
            </div>

            {/* Swedish Authenticity */}
            <div className="flex items-center px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform duration-300" style={{ background: 'linear-gradient(to right, #4CC8ED, #A8E6F7)' }}>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#221D58' }}>
                <path d="M12 2L13.09 8.26L20 7.27L14.64 12.73L19.18 17.27L12 13.73L4.82 17.27L9.36 12.73L4 7.27L10.91 8.26L12 2Z"/>
              </svg>
              <span className="font-black text-xs" style={{ color: '#221D58' }}>SWEDISH AUTHENTICITY</span>
            </div>

            {/* Premium Texture */}
            <div className="flex items-center px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform duration-300" style={{ background: 'linear-gradient(to right, #F5ED3A, #FFF8B8)' }}>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#221D58' }}>
                <path d="M12,2A7,7 0 0,1 19,9C19,11.38 17.81,13.47 16,14.74V17A1,1 0 0,1 15,18H9A1,1 0 0,1 8,17V14.74C6.19,13.47 5,11.38 5,9A7,7 0 0,1 12,2M9,21V20H15V21A1,1 0 0,1 14,22H10A1,1 0 0,1 9,21M12,4A5,5 0 0,0 7,9C7,11 8.68,12.65 10.5,12.91L11,19H13L13.5,12.91C15.32,12.65 17,11 17,9A5,5 0 0,0 12,4Z"/>
              </svg>
              <span className="font-black text-xs" style={{ color: '#221D58' }}>PREMIUM TEXTURE</span>
            </div>

            {/* European Quality */}
            <div className="flex items-center px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform duration-300" style={{ background: 'linear-gradient(to right, #4CC8ED, #A8E6F7)' }}>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#221D58' }}>
                <path d="M5,16L3,5H1V3H4L6,14H18V16H5M7,18A2,2 0 0,1 9,20A2,2 0 0,1 7,22A2,2 0 0,1 5,20A2,2 0 0,1 7,18M17,18A2,2 0 0,1 19,20A2,2 0 0,1 17,22A2,2 0 0,1 15,20A2,2 0 0,1 17,18M7.17,14L6.1,10H15.55L14.77,14H7.17Z"/>
              </svg>
              <span className="font-black text-xs" style={{ color: '#221D58' }}>EUROPEAN QUALITY</span>
            </div>

            {/* Natural Colors */}
            <div className="flex items-center px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform duration-300" style={{ background: 'linear-gradient(to right, #F5ED3A, #FFF8B8)' }}>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#221D58' }}>
                <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"/>
              </svg>
              <span className="font-black text-xs" style={{ color: '#221D58' }}>NATURAL COLORS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section - Sour Oozies, Crystal Candy, Pick & Mix and Candy Bags */}
      <section className="relative py-20 border-b-2 border-kato-navy" style={{ backgroundColor: '#221D58' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* First Row - Sour Oozies and Crystal Candy */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start mb-16">
            {/* Sour Oozies - Left Side - Links to Products Page Sour Oozies Section */}
            <div className="flex flex-col items-center">
              <h3 className="text-4xl md:text-5xl font-black mb-8" style={{ color: '#F5ED3A' }}>
                SOUR OOZIES
              </h3>
              <a 
                href="/products#sour-oozies" 
                className="group flex justify-center items-center transform transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_279ff17a-327c-4c2c-8938-25803493c98b/artifacts/wwe06g1i_Untitled30_20260302103203.png" 
                  alt="Sour Oozies Collection" 
                  className="w-full max-w-sm h-auto object-contain drop-shadow-2xl group-hover:drop-shadow-3xl transition-all duration-300"
                />
              </a>
            </div>

            {/* Crystal Candy - Right Side - Links to Products Page Crystal Candy Section */}
            <div className="flex flex-col items-center">
              <h3 className="text-4xl md:text-5xl font-black mb-8" style={{ color: '#F5ED3A' }}>
                CRYSTAL CANDY
              </h3>
              <a 
                href="/products#crystal-candy" 
                className="group flex justify-center items-center transform transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_279ff17a-327c-4c2c-8938-25803493c98b/artifacts/xbitzk79_Untitled30_20260302103811.png" 
                  alt="Crystal Candy Collection" 
                  className="w-full max-w-sm h-auto object-contain drop-shadow-2xl group-hover:drop-shadow-3xl transition-all duration-300"
                />
              </a>
            </div>
          </div>

          {/* Second Row - Pick & Mix and Candy Bags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
            {/* Pick & Mix - Left Side - Links to Products Page Bulk Section */}
            <div className="flex flex-col items-center">
              <h3 className="text-4xl md:text-5xl font-black mb-8" style={{ color: '#F5ED3A' }}>
                PICK & MIX
              </h3>
              <a 
                href="/products#bulk-options" 
                className="group flex justify-center items-center transform transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_kato-mito-upgrade/artifacts/m5t21res_Untitled30_20251120022531.png" 
                  alt="Pick & Mix - Bulk Options" 
                  className="w-full max-w-sm h-auto object-contain drop-shadow-2xl group-hover:drop-shadow-3xl transition-all duration-300"
                />
              </a>
            </div>

            {/* Candy Bags - Right Side - Links to Products Page Banner */}
            <div className="flex flex-col items-center">
              <h3 className="text-4xl md:text-5xl font-black mb-8" style={{ color: '#F5ED3A' }}>
                Swedish Style Candy
              </h3>
              <a 
                href="/products" 
                className="group flex justify-center items-center transform transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_kato-mito-upgrade/artifacts/qmegnbvz_Untitled34_20251120054843.png" 
                  alt="Candy Bags - Variety Packs" 
                  className="w-full max-w-sm h-auto object-contain drop-shadow-2xl group-hover:drop-shadow-3xl transition-all duration-300"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Candy For Business Section */}
      <section className="relative py-20 bg-yellow-200/30 border-b-2 border-kato-navy overflow-hidden">
        {/* Floating Candy Icons */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-12 opacity-40 animate-fadeInOut delay-0">
            <img src="https://cdn-icons-png.flaticon.com/128/1391/1391858.png" alt="sweets" className="w-12 h-12" />
          </div>
          <div className="absolute top-32 right-20 opacity-30 animate-fadeInOut delay-1000">
            <img src="https://cdn-icons-png.flaticon.com/128/1888/1888859.png" alt="candy" className="w-10 h-10" />
          </div>
          <div className="absolute bottom-24 left-1/4 opacity-50 animate-fadeInOut delay-2000">
            <img src="https://cdn-icons-png.flaticon.com/128/2454/2454268.png" alt="sweets collection" className="w-16 h-16" />
          </div>
          <div className="absolute top-16 left-1/3 opacity-35 animate-fadeInOut delay-500">
            <img src="https://cdn-icons-png.flaticon.com/128/1888/1888900.png" alt="candy wrapper" className="w-10 h-10" />
          </div>
          <div className="absolute bottom-32 right-12 opacity-45 animate-fadeInOut delay-1500">
            <img src="https://cdn-icons-png.flaticon.com/128/2575/2575920.png" alt="candy" className="w-12 h-12" />
          </div>
          <div className="absolute top-40 right-1/3 opacity-25 animate-fadeInOut delay-3000">
            <img src="https://cdn-icons-png.flaticon.com/128/1035/1035121.png" alt="lollipop" className="w-10 h-10" />
          </div>
          <div className="absolute bottom-16 left-20 opacity-40 animate-fadeInOut delay-2500">
            <img src="https://cdn-icons-png.flaticon.com/128/2608/2608473.png" alt="cotton candy" className="w-12 h-12" />
          </div>
          <div className="absolute top-24 left-2/3 opacity-30 animate-fadeInOut delay-1200">
            <img src="https://cdn-icons-png.flaticon.com/128/883/883431.png" alt="lollipop" className="w-10 h-10" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-5xl md:text-6xl font-black text-kato-navy mb-12">
              CANDY FOR BUSINESS
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* Top Left - Serving Partners */}
              <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:scale-105 transition-transform">
                <div className="w-20 h-20 bg-kato-cyan rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-kato-navy mb-3">500+ Global Partners</h3>
                <p className="text-lg font-medium text-gray-600">Making happy memories with retailers and candy lovers across 45+ countries worldwide.</p>
              </div>

              {/* Top Right - 4OZ Packs */}
              <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:scale-105 transition-transform">
                <div className="w-20 h-20 bg-kato-yellow rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Package className="h-10 w-10 text-kato-navy" />
                </div>
                <h3 className="text-2xl font-black text-kato-navy mb-3">Variety Packs</h3>
                <p className="text-lg font-medium text-gray-600">Our Mixed Variety Candy Bags are shelf-ready and designed for quick and easy display. Each bag contains a colourful assortment of our best-loved treats, making it ideal for impulse purchases and seasonal promotions.</p>
              </div>

              {/* Bottom Left - 5LB Bulk */}
              <div id="home-bulk-options" className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:scale-105 transition-transform">
                <div className="w-20 h-20 bg-kato-navy rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Zap className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-kato-navy mb-3">Bulk Options for Pick & Mix</h3>
                <p className="text-lg font-medium text-gray-600">Ready-to-scoop bulk candy that's ideal for pick & mix stores, candy shops, and retailers. Choose from our wide range of sweets to keep your customers coming back for more.</p>
              </div>

              {/* Bottom Right - Custom Creations */}
              <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:scale-105 transition-transform">
                <div className="w-20 h-20 bg-kato-cyan rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Star className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-black text-kato-navy mb-3">Custom Creations</h3>
                <p className="text-lg font-medium text-gray-600">Unique shapes, flavors, and colors crafted for your brand.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/products"
                className="bg-kato-navy text-white px-12 py-5 rounded-full text-xl font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-2xl hover:scale-105 inline-flex items-center justify-center group"
              >
                EXPLORE PRODUCTS
                <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="border-3 border-kato-navy text-kato-navy px-12 py-5 rounded-full text-xl font-black hover:bg-kato-navy hover:text-white transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
              >
                GET QUOTE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Big Impact Numbers */}
      <section className="py-20 bg-white relative overflow-hidden border-b-2 border-kato-navy">
        <div className="absolute inset-0 bg-gradient-to-r from-kato-light-gray to-white"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-kato-navy mb-4">
              BY THE <span className="text-kato-cyan">NUMBERS</span>
            </h2>
            <p className="text-xl text-gray-600">Trusted by businesses worldwide across 6 continents</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group hover:scale-110 transition-transform duration-300" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="text-6xl md:text-7xl font-black text-kato-navy mb-4 group-hover:text-kato-cyan transition-colors">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-lg md:text-xl font-semibold uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Bold Cards */}
      <section className="py-20 bg-kato-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-kato-navy to-purple-900"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              WHY CHOOSE <span className="text-kato-yellow">KATO MITO</span>?
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              The perfect blend of Swedish tradition and modern innovation for your business success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = {
                Candy: Package,
                Palette: Star,
                Package: Package,
                Award: Award
              }[feature.icon] || Package;

              return (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 group"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="w-16 h-16 bg-kato-yellow rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="h-8 w-8 text-kato-navy" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-kato-yellow transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Removed - Our Offerings */}

      {/* CTA Section - Big Call to Action */}
      <section className="py-20 bg-yellow-200/30 text-kato-navy relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23FFFFFF%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%223%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="animate-fadeInUp">
            <h2 className="text-5xl md:text-7xl font-black mb-6">
              READY TO PARTNER?
            </h2>
            <p className="text-2xl mb-12 opacity-90 max-w-4xl mx-auto font-medium">
              Join over <span className="font-black">500+ global partners across 45+ countries</span> who trust Kato Mito 
              for premium Swedish-style candy. Let's make happy memories together worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="bg-kato-navy text-white px-12 py-6 rounded-full text-2xl font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-2xl hover:scale-105 inline-flex items-center justify-center group"
              >
                GET STARTED TODAY
                <ArrowRight className="ml-3 h-8 w-8 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link
                to="/products"
                className="border-4 border-kato-navy text-kato-navy px-12 py-6 rounded-full text-2xl font-black hover:bg-kato-navy hover:text-white transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
              >
                VIEW PRODUCTS
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;